import React, { useState, useEffect } from 'react';
import {
 Box,
 Card,
 CardContent,
 Typography,
 Button,
 Tabs,
 Tab,
 Grid,
 AppBar,
 Toolbar,
 IconButton,
 Badge,
 Avatar,
 Menu,
 MenuItem,
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 TextField,
 Chip,
 LinearProgress,
 Paper,
 List,
 ListItem,
 ListItemText,
 ListItemAvatar,
 ListItemSecondaryAction,
 Switch,
 FormControlLabel,
 Divider
} from '@mui/material';
import {
 Dashboard,
 Group,
 Person,
 Chat,
 Notifications,
 Settings,
 AccountCircle,
 Logout,
 Add,
 Refresh,
 TrendingUp,
 Assignment,
 Timeline,
 Code,
 BugReport,
 Design,
 Storage,
 CheckCircle,
 Pending,
 PlayArrow
} from '@mui/icons-material';
import IndividualRoleViewer from './IndividualRoleViewer';
import TeamRoleReplica from './TeamRoleReplica';
import TeamChatRoom from './TeamChatRoom';

const RoleDashboard = () => {
 const [currentTab, setCurrentTab] = useState(0);
 const [chatOpen, setChatOpen] = useState(false);
 const [settingsOpen, setSettingsOpen] = useState(false);
 const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
 const [notifications, setNotifications] = useState([]);
 const [settings, setSettings] = useState({
 notifications: true,
 darkMode: false,
 autoSave: true,
 showProgress: true
 });

 // Mock user data
 const currentUser = {
 id: 'user1',
 name: 'Alex Johnson',
 role: 'Frontend Developer',
 avatar: 'A',
 email: 'alex.johnson@example.com',
 team: 'Development Team Alpha'
 };

 // Mock notifications
 const mockNotifications = [
 {
 id: 1,
 title: 'Task Completed',
 message: 'Sarah Chen completed the backend API development',
 timestamp: new Date(Date.now() - 1000 * 60 * 5),
 type: 'success',
 read: false
 },
 {
 id: 2,
 title: 'New Message',
 message: 'Mike Rodriguez sent a message in team chat',
 timestamp: new Date(Date.now() - 1000 * 60 * 15),
 type: 'info',
 read: false
 },
 {
 id: 3,
 title: 'Deadline Reminder',
 message: 'Phase 2 deadline is approaching (2 days left)',
 timestamp: new Date(Date.now() - 1000 * 60 * 30),
 type: 'warning',
 read: true
 },
 {
 id: 4,
 title: 'Code Review',
 message: 'Your code review is ready for the authentication module',
 timestamp: new Date(Date.now() - 1000 * 60 * 60),
 type: 'info',
 read: true
 }
 ];

 // Mock team data
 const teamData = {
 name: 'Development Team Alpha',
 project: 'Blog Platform with Authentication',
 members: [
 {
 id: 'person1',
 name: 'Alex Johnson',
 role: 'Frontend Developer',
 avatar: 'A',
 color: '#4CAF50',
 status: 'online',
 tasks: { completed: 8, total: 12 }
 },
 {
 id: 'person2',
 name: 'Sarah Chen',
 role: 'Backend Developer',
 avatar: 'S',
 color: '#2196F3',
 status: 'online',
 tasks: { completed: 10, total: 14 }
 },
 {
 id: 'person3',
 name: 'Mike Rodriguez',
 role: 'UI/UX Developer',
 avatar: 'M',
 color: '#FF9800',
 status: 'away',
 tasks: { completed: 6, total: 10 }
 },
 {
 id: 'person4',
 name: 'Emily Watson',
 role: 'QA/Testing Engineer',
 avatar: 'E',
 color: '#9C27B0',
 status: 'online',
 tasks: { completed: 9, total: 12 }
 }
 ],
 progress: 68,
 deadline: '2024-02-15'
 };

 useEffect(() => {
 setNotifications(mockNotifications);
 }, []);

 const handleTabChange = (event, newValue) => {
 setCurrentTab(newValue);
 };

 const handleProfileMenuOpen = (event) => {
 setProfileMenuAnchor(event.currentTarget);
 };

 const handleProfileMenuClose = () => {
 setProfileMenuAnchor(null);
 };

 const handleSettingsChange = (setting, value) => {
 setSettings(prev => ({
 ...prev,
 [setting]: value
 }));
 };

 const getNotificationIcon = (type) => {
 switch (type) {
 case 'success': return <CheckCircle color="success" />;
 case 'warning': return <Pending color="warning" />;
 case 'info': return <PlayArrow color="info" />;
 default: return <Notifications />;
 }
 };

 const formatTime = (timestamp) => {
 const now = new Date();
 const diff = now - timestamp;
 const minutes = Math.floor(diff / (1000 * 60));
 const hours = Math.floor(diff / (1000 * 60 * 60));
 const days = Math.floor(diff / (1000 * 60 * 60 * 24));

 if (minutes < 1) return 'Just now';
 if (minutes < 60) return `${minutes}m ago`;
 if (hours < 24) return `${hours}h ago`;
 if (days < 7) return `${days}d ago`;
 return timestamp.toLocaleDateString();
 };

 const renderTabContent = () => {
 switch (currentTab) {
 case 0:
 return <IndividualRoleViewer />;
 case 1:
 return <TeamRoleReplica />;
 case 2:
 return (
 <Box sx={{ p: 3 }}>
 <Typography variant="h5" gutterBottom>
 Project Analytics
 </Typography>
 <Grid container spacing={3}>
 <Grid item xs={12} md={6}>
 <Card>
 <CardContent>
 <Typography variant="h6" gutterBottom>
 Team Progress
 </Typography>
 <Box sx={{ mb: 2 }}>
 <Typography variant="body2" color="text.secondary">
 Overall Progress: {teamData.progress}%
 </Typography>
 <LinearProgress 
 variant="determinate" 
 value={teamData.progress} 
 sx={{ height: 8, borderRadius: 4, mt: 1 }}
 />
 </Box>
 <Typography variant="body2" color="text.secondary">
 Deadline: {new Date(teamData.deadline).toLocaleDateString()}
 </Typography>
 </CardContent>
 </Card>
 </Grid>
 <Grid item xs={12} md={6}>
 <Card>
 <CardContent>
 <Typography variant="h6" gutterBottom>
 Team Members
 </Typography>
 <List dense>
 {teamData.members.map((member) => (
 <ListItem key={member.id}>
 <ListItemAvatar>
 <Avatar sx={{ bgcolor: member.color }}>
 {member.avatar}
 </Avatar>
 </ListItemAvatar>
 <ListItemText
 primary={member.name}
 secondary={`${member.tasks.completed}/${member.tasks.total} tasks completed`}
 />
 <ListItemSecondaryAction>
 <Chip 
 label={member.status} 
 size="small" 
 color={member.status === 'online' ? 'success' : 'default'}
 />
 </ListItemSecondaryAction>
 </ListItem>
 ))}
 </List>
 </CardContent>
 </Card>
 </Grid>
 </Grid>
 </Box>
 );
 default:
 return <IndividualRoleViewer />;
 }
 };

 return (
 <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
 {/* Header */}
 <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
 <Toolbar>
 <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
 Role Dashboard
 </Typography>
 
 <IconButton color="inherit" onClick={() => setChatOpen(true)}>
 <Badge badgeContent={2} color="error">
 <Chat />
 </Badge>
 </IconButton>
 
 <IconButton color="inherit">
 <Badge badgeContent={notifications.filter(n => !n.read).length} color="error">
 <Notifications />
 </Badge>
 </IconButton>
 
 <IconButton color="inherit" onClick={() => setSettingsOpen(true)}>
 <Settings />
 </IconButton>
 
 <IconButton color="inherit" onClick={handleProfileMenuOpen}>
 <Avatar sx={{ bgcolor: '#4CAF50', width: 32, height: 32 }}>
 {currentUser.avatar}
 </Avatar>
 </IconButton>
 </Toolbar>
 </AppBar>

 {/* Profile Menu */}
 <Menu
 anchorEl={profileMenuAnchor}
 open={Boolean(profileMenuAnchor)}
 onClose={handleProfileMenuClose}
 >
 <MenuItem onClick={handleProfileMenuClose}>
 <AccountCircle sx={{ mr: 1 }} />
 Profile
 </MenuItem>
 <MenuItem onClick={handleProfileMenuClose}>
 <Settings sx={{ mr: 1 }} />
 Settings
 </MenuItem>
 <Divider />
 <MenuItem onClick={handleProfileMenuClose}>
 <Logout sx={{ mr: 1 }} />
 Logout
 </MenuItem>
 </Menu>

 {/* Main Content */}
 <Box sx={{ display: 'flex' }}>
 {/* Sidebar */}
 <Paper sx={{ 
 width: 250, 
 minHeight: 'calc(100vh - 64px)', 
 borderRadius: 0,
 borderRight: 1,
 borderColor: 'divider'
 }}>
 <Box sx={{ p: 2 }}>
 <Typography variant="h6" gutterBottom>
 {teamData.project}
 </Typography>
 <Typography variant="body2" color="text.secondary" gutterBottom>
 {teamData.name}
 </Typography>
 <Box sx={{ mb: 2 }}>
 <Typography variant="body2" color="text.secondary">
 Progress: {teamData.progress}%
 </Typography>
 <LinearProgress 
 variant="determinate" 
 value={teamData.progress} 
 sx={{ height: 6, borderRadius: 3, mt: 1 }}
 />
 </Box>
 </Box>
 
 <Divider />
 
 <Box sx={{ p: 2 }}>
 <Typography variant="subtitle2" gutterBottom>
 Quick Actions
 </Typography>
 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
 <Button 
 variant="outlined" 
 size="small" 
 startIcon={<Add />}
 onClick={() => setChatOpen(true)}
 >
 New Chat
 </Button>
 <Button 
 variant="outlined" 
 size="small" 
 startIcon={<Refresh />}
 >
 Refresh Data
 </Button>
 </Box>
 </Box>
 </Paper>

 {/* Main Content Area */}
 <Box sx={{ flex: 1 }}>
 <Paper sx={{ borderRadius: 0 }}>
 <Tabs 
 value={currentTab} 
 onChange={handleTabChange}
 variant="fullWidth"
 >
 <Tab 
 icon={<Person />} 
 label="Individual Roles" 
 iconPosition="start"
 />
 <Tab 
 icon={<Group />} 
 label="Team Roles" 
 iconPosition="start"
 />
 <Tab 
 icon={<TrendingUp />} 
 label="Analytics" 
 iconPosition="start"
 />
 </Tabs>
 </Paper>
 
 <Box sx={{ flex: 1 }}>
 {renderTabContent()}
 </Box>
 </Box>
 </Box>

 {/* Chat Room Dialog */}
 <TeamChatRoom
 open={chatOpen}
 onClose={() => setChatOpen(false)}
 teamMembers={teamData.members}
 projectTitle={teamData.project}
 />

 {/* Settings Dialog */}
 <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
 <DialogTitle>Settings</DialogTitle>
 <DialogContent>
 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
 <FormControlLabel
 control={
 <Switch
 checked={settings.notifications}
 onChange={(e) => handleSettingsChange('notifications', e.target.checked)}
 />
 }
 label="Enable Notifications"
 />
 <FormControlLabel
 control={
 <Switch
 checked={settings.darkMode}
 onChange={(e) => handleSettingsChange('darkMode', e.target.checked)}
 />
 }
 label="Dark Mode"
 />
 <FormControlLabel
 control={
 <Switch
 checked={settings.autoSave}
 onChange={(e) => handleSettingsChange('autoSave', e.target.checked)}
 />
 }
 label="Auto Save"
 />
 <FormControlLabel
 control={
 <Switch
 checked={settings.showProgress}
 onChange={(e) => handleSettingsChange('showProgress', e.target.checked)}
 />
 }
 label="Show Progress Indicators"
 />
 </Box>
 </DialogContent>
 <DialogActions>
 <Button onClick={() => setSettingsOpen(false)}>Close</Button>
 <Button variant="contained" onClick={() => setSettingsOpen(false)}>Save</Button>
 </DialogActions>
 </Dialog>
 </Box>
 );
};

export default RoleDashboard;




